import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
  getErrorMessage,
  validate,
  ValType,
} from "../../utils/ValidationFunctions";
import { prefectureOptions } from "../../utils/GeneralUtils";

import ErrorBanner from "./ErrorBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type Props = {
  handler: (value: File | string | undefined, name: string) => void;

  type: string;
  label: string;
  name: string;
  val_type?: ValType;
  error?: boolean;
  initial_value?: string | number;
  placeholder?: string;
  id?: string;
  filetypes?: string;
  multiple_upload?: boolean;
  rows?: number;
  style_class?: string;
  label_class?: string;
  ul_class?: string;
  options?: Array<[string, string]>;
  list_class?: Array<string>;
  disabled?: boolean;
  select_class?: string;
  holder_class?: string;
  input_wrap_class?: string;
  min_date?: string;
  label_supplement?: React.ReactNode;
  merit_text?: string;
};

const FormInput: React.FC<Props> = ({
  handler,
  type,
  label,
  name,
  val_type,
  initial_value,
  placeholder,
  id,
  filetypes,
  multiple_upload,
  rows,
  style_class,
  label_class,
  ul_class,
  options,
  list_class,
  disabled,
  select_class,
  holder_class,
  input_wrap_class,
  min_date,
  label_supplement,
  merit_text,
}) => {
  const { t } = useTranslation();

  const [error, setError] = useState(false);
  const [value, setValue] = useState("");
  const [isWaiting, setIsWaiting] = useState(false);

  useEffect(() => {
    if (initial_value !== undefined && initial_value !== null) {
      setValue(initial_value.toString());
    }
  }, [initial_value]);

  async function verifyInput(
    event: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >,
    isDate?: boolean,
    removeWhiteSpace: boolean = false
  ) {
    let val;
    if (isDate) {
      const date = new Date(event.target.value);
      date.setHours(0, 0, 0, 0);
      val = date.getTime() / 1000;
    } else {
      val = event.target.value;
    }

    if (removeWhiteSpace) {
      val = val.toString().replace(/\s+/g, "");
    }

    if (Number.isNaN(val)) {
      val = "";
    }

    setValue(val.toString());
    setError(false);

    if (val_type == ValType.Email) {
      setIsWaiting(true);
    }

    if (val_type !== undefined) {
      try {
        handler(undefined, event.target.name);

        const result = await validate(val, val_type);
        setIsWaiting(false);
        if (result) {
          setError(false);
          if (handler) {
            handler(val.toString(), event.target.name);
          }
        } else {
          setError(true);

          handler(undefined, event.target.name);
        }
      } catch (error) {
        setError(true);

        handler(undefined, event.target.name);
      }
    } else {
      if (handler) {
        handler(val.toString(), event.target.name);
      }
    }
  }

  async function verifyFiles(event: React.ChangeEvent<HTMLInputElement>) {
    const val = event.target.files !== null ? event.target.files[0] : undefined;
    setValue(event.target.value);

    if (val_type !== undefined && val !== undefined) {
      try {
        const result = await validate(val, val_type);
        if (result) {
          setError(false);
          if (handler) {
            handler(val, event.target.name);
          }
        } else {
          setError(true);

          handler(undefined, event.target.name);
        }
      } catch (error) {
        setError(true);

        handler(undefined, event.target.name);
      }
    } else {
      if (handler) {
        handler(val, event.target.name);
      }
    }
  }

  function genRadioButtons(options: Array<[string, string]>) {
    const buttons = options.map((val: [string, string], i: number) => (
      <li className={style_class} key={i}>
        <input
          type="radio"
          id={val[0]}
          name={name}
          value={val[0]}
          onChange={verifyInput}
          checked={val[0] == value.split(",").shift() ? true : false}
        />
        <label htmlFor={val[0]}>{t(val[1])}</label>
      </li>
    ));
    return <ul className={ul_class}>{buttons}</ul>;
  }

  function genSelectBox(options: Array<[string, string]>) {
    const opts = options.map((val: [string, string], i: number) => (
      <option value={val[0]} key={i}>
        {t(val[1])}
      </option>
    ));

    if (input_wrap_class) {
      return (
        <div className={input_wrap_class}>
          <select
            className={value !== "" ? select_class : holder_class}
            id={id}
            name={name}
            onChange={verifyInput}
            value={value}
          >
            {placeholder ? (
              <option
                disabled={
                  value && val_type !== undefined && val_type == ValType.NotNull
                    ? true
                    : false
                }
              >
                {t(placeholder)}
              </option>
            ) : null}
            {opts}
          </select>
          <FontAwesomeIcon icon={["fal", "chevron-down"]} />
        </div>
      );
    } else {
      return (
        <select
          className={value !== "" ? select_class : holder_class}
          id={id}
          name={name}
          onChange={verifyInput}
          value={value}
        >
          {placeholder ? (
            <option
              disabled={
                value && val_type !== undefined && val_type == ValType.NotNull
                  ? true
                  : false
              }
            >
              {t(placeholder)}
            </option>
          ) : null}
          {opts}
        </select>
      );
    }
  }

  function genPrefectureSelectBox() {
    const opts = prefectureOptions.map((val: string, i: number) => (
      <option value={val} key={i}>
        {val}
      </option>
    ));
    return (
      <select
        className={value !== "" ? select_class : holder_class}
        id={id}
        name={name}
        onChange={verifyInput}
        value={value}
      >
        {placeholder ? <option value={""}>{t(placeholder)}</option> : null}
        {opts}
      </select>
    );
  }

  function genInput() {
    switch (type) {
      case "text":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            {label_supplement}
            {merit_text ? <p>{merit_text}</p> : null}
            <input
              className={style_class}
              type="text"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              placeholder={placeholder}
              disabled={disabled}
            />
          </>
        );
      case "textarea":
        // 高さを自動で調整する機能があったほうがいいかもしれない
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            {merit_text ? <p>{merit_text}</p> : null}
            <textarea
              className={style_class}
              id={id}
              name={name}
              value={value}
              rows={rows}
              onChange={verifyInput}
              placeholder={placeholder}
              disabled={disabled}
            >
              {/* {placeholder} */}
            </textarea>
          </>
        );
      case "file":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            <input
              className={style_class}
              type="file"
              id={id}
              name={name}
              onChange={verifyFiles}
              accept={filetypes}
              multiple={multiple_upload}
              disabled={disabled}
            />
          </>
        );
      case "hidden":
        return (
          <>
            <input
              className={style_class}
              type="hidden"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
            />
          </>
        );
      case "date": {
        let ymd = "";
        if (value) {
          const date = new Date(parseInt(value) * 1000);

          const year = date.getFullYear();

          //monthは0 ~ 11
          const month =
            date.getMonth() + 1 < 10
              ? `0${date.getMonth() + 1}`
              : date.getMonth() + 1;
          const day =
            date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();

          ymd = `${year}-${month}-${day}`;
        }

        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
            {merit_text ? <p>{merit_text}</p> : null}
            <div className={input_wrap_class}>
              <input
                className={style_class}
                type="date"
                id={id}
                name={name}
                value={ymd}
                onChange={(e) => verifyInput(e, true)}
                min={min_date ? min_date : ""}
              />
              <FontAwesomeIcon icon={["far", "calendar"]} />
            </div>
          </>
        );
      }
      case "radio":
        if (options !== undefined) {
          return (
            <>
              <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
              <div className={label_class}>{t(label)}</div>
              {merit_text ? <p>{merit_text}</p> : null}
              {genRadioButtons(options)}
            </>
          );
        } else {
          return null;
        }

      case "select":
        if (options !== undefined) {
          return (
            <>
              <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
              <div className={label_class}>{t(label)}</div>
              {merit_text ? <p>{merit_text}</p> : null}
              {genSelectBox(options)}
            </>
          );
        } else {
          return null;
        }

      case "prefecture":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <div className={label_class}>{t(label)}</div>
            {genPrefectureSelectBox()}
          </>
        );
      case "checkbox":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
            <input
              className={style_class}
              type="checkbox"
              id={id}
              name={name}
              onChange={verifyInput}
              checked={initial_value === "true" ? true : false}
            />
          </>
        );
      case "verification_code":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <input
              className={style_class}
              type="text"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              style={{ textTransform: "uppercase" }}
              placeholder={placeholder}
            />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
          </>
        );
      case "password":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
            <input
              className={style_class}
              type="password"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              placeholder={placeholder}
            />
          </>
        );
      case "login_password":
        return (
          <>
            <input
              className={style_class}
              type="password"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              placeholder={placeholder}
            />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
          </>
        );
      case "textBeforeLabel":
        // placeholderが上に移動するやつのために input と label を逆にしている
        return (
          <>
            <input
              className={style_class}
              type="text"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              placeholder={placeholder}
            />

            {isWaiting ? <FontAwesomeIcon icon={["fas", "spinner"]} /> : null}

            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
          </>
        );

      case "passwordBeforeLabel":
        // placeholderが上に移動するやつのために input と label を逆にしている
        return (
          <>
            <input
              className={style_class}
              type="password"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              placeholder={placeholder}
            />
            <label className={label_class} htmlFor={name}>
              {t(label)}
            </label>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
          </>
        );
      case "color":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            {merit_text ? <p>{merit_text}</p> : null}
            <input
              className={style_class}
              type="color"
              id={id}
              name={name}
              value={value}
              onChange={verifyInput}
              disabled={disabled}
            />
          </>
        );
      //   case "team":
      //     return (
      //       <>
      //         <div className={btn_wrap_class}>
      //           <button
      //             onClick={() => {
      //               document.getElementById("text").style.display = "none";
      //               document.getElementById("select").style.display = "";
      //             }}
      //           >
      //             登録済みのチームから選択
      //           </button>
      //           <button
      //             onClick={() => {
      //               document.getElementById("select").style.display = "none";
      //               document.getElementById("text").style.display = "";
      //             }}
      //           >
      //             新しいチームを登録
      //           </button>
      //         </div>
      //         <label className={label_class}>{label}</label>
      //         {genSelectBox(args)}
      //         <input
      //           className={style_class}
      //           type="text"
      //           id="text"
      //           name="team"
      //           placeholder={text_placeholder}
      //         />
      //       </>
      //     );
      case "admin_offer_status":
        return (
          <ul>
            {options?.map((val: [string, string], i: number) => (
              <li className={`${style_class} ${list_class?.[i]}`} key={i}>
                <input
                  type="radio"
                  id={val[0]}
                  name={name}
                  value={val[0]}
                  onChange={verifyInput}
                  checked={val[0] == value.split(",").shift() ? true : false}
                />
                <label htmlFor={val[0]}>{t(val[1])}</label>
              </li>
            ))}
          </ul>
        );
      case "positive_int":
        return (
          <>
            <ErrorBanner warn={error} message={getErrorMessage(val_type)} />
            <label className={label_class} htmlFor={id}>
              {t(label)}
            </label>
            {label_supplement}
            {merit_text ? <p>{merit_text}</p> : null}
            <input
              className={style_class}
              type="text"
              id={id}
              name={name}
              value={value}
              onChange={(e) => verifyInput(e, false, true)}
              placeholder={placeholder}
              disabled={disabled}
              inputMode="numeric"
            />
          </>
        );
      default:
        return null;
    }
  }

  return genInput();
};

export default FormInput;

// <div className={styles["f-adminOfferStatus__status-menu"]}>
//   <div
//     className={styles["f-adminOfferStatus__status-menu__header"]}
//   >
//     <span>ステータス変更</span>
//     <FontAwesomeIcon icon={["fas", "times"]} />
//   </div>
//   <div
//     className={`${styles["f-adminOfferStatus__status-menu__type"]} ${styles["pending"]}`}
//   >
//     <FontAwesomeIcon
//       icon={
//         status == "確認待ち"
//           ? ["far", "dot-circle"]
//           : ["far", "circle"]
//       }
//     />
//     <span>確認待ち</span>
//   </div>
//   <div
//     className={`${styles["f-adminOfferStatus__status-menu__type"]} ${styles["progress"]}`}
//   >
//     <FontAwesomeIcon
//       icon={
//         status == "対応中"
//           ? ["far", "dot-circle"]
//           : ["far", "circle"]
//       }
//     />
//     <span>対応中</span>
//   </div>
//   <div
//     className={`${styles["f-adminOfferStatus__status-menu__type"]} ${styles["complete"]}`}
//   >
//     <FontAwesomeIcon
//       icon={
//         status == "完了" ? ["far", "dot-circle"] : ["far", "circle"]
//       }
//     />
//     <span>完了</span>
//   </div>
//   <div
//     className={`${styles["f-adminOfferStatus__status-menu__type"]} ${styles["cancel"]}`}
//   >
//     <FontAwesomeIcon
//       icon={
//         status == "キャンセル"
//           ? ["far", "dot-circle"]
//           : ["far", "circle"]
//       }
//     />
//     <span>キャンセル</span>
//   </div>
// </div>
